import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { MenuItem, Select } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import { useEffect, useMemo } from 'react';

const toString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
};

const DependentFieldPickerFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      controllingField: z
        .string()
        .describe(
          'The field whose values will control the display of the dependent field options',
        ),
      options: z
        .array(
          z.object({
            controllingValues: z
              .array(
                z
                  .string()
                  .describe(
                    'The values of the controlling field that will trigger the display of this options',
                  ),
              )
              .min(1),
            values: z
              .array(
                z
                  .union([z.string(), z.record(z.any())])
                  .describe(
                    'The values to be shown when the controlling field matches the controlling values',
                  ),
              )
              .min(1),
            valueNames: z
              .array(z.string().describe('The display names for the values'))
              .min(1),
          }),
        )
        .min(1),
    }),
});

export const DependentFieldPickerSchema = DependentFieldPickerFieldSchema;

type DependentFieldPickerProps = typeof DependentFieldPickerFieldSchema.TProps;

export const DependentFieldPicker = ({
  idSchema,
  schema,
  uiSchema,
  onChange,
  rawErrors,
  formData,
  formContext,
}: DependentFieldPickerProps) => {
  const formContextData = formContext?.formData;

  const controllingFieldValue =
    formContextData?.[uiSchema?.['ui:options']?.controllingField as string];

  const controllingFieldStringValue = useMemo(() => {
    if (
      controllingFieldValue === undefined ||
      controllingFieldValue === null ||
      controllingFieldValue === ''
    ) {
      return undefined;
    }

    return toString(controllingFieldValue);
  }, [controllingFieldValue]);

  const allOptions = useMemo(() => {
    return uiSchema?.['ui:options']?.options ?? [];
  }, [uiSchema]);

  const filteredOptions = useMemo(() => {
    if (!controllingFieldStringValue) {
      return [];
    }

    const filteredOptionsMap = new Map<string, string>();

    allOptions.forEach(option => {
      const controllingValues = option.controllingValues ?? [];

      const matches = controllingValues
        .map(v => toString(v))
        .includes(controllingFieldStringValue);

      if (matches) {
        option.values?.forEach((value, index) => {
          if (value === undefined || value === null) {
            return;
          }

          const serializedValue = toString(value);

          if (!filteredOptionsMap.has(serializedValue)) {
            filteredOptionsMap.set(
              serializedValue,
              option.valueNames?.[index] ?? serializedValue,
            );
          }
        });
      }
    });

    return Array.from(filteredOptionsMap, ([value, label]) => ({
      value,
      label,
    }));
  }, [allOptions, controllingFieldStringValue]);

  const shouldHideField = filteredOptions.length === 0;

  useEffect(() => {
    if (shouldHideField && formData) {
      onChange(undefined as unknown as string);
      return;
    }

    if (
      formData &&
      !filteredOptions.some(option => option.value === formData)
    ) {
      onChange(undefined as unknown as string);
    }
  }, [filteredOptions, formData, onChange, shouldHideField]);

  if (shouldHideField) {
    return null;
  }

  return (
    <FormControl
      margin="normal"
      required={!shouldHideField}
      error={rawErrors?.length > 0 && !formData}
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title ?? 'Select an option'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value as string)}
      >
        {filteredOptions.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {schema.description && (
        <FormHelperText id={idSchema.$id}>{schema.description}</FormHelperText>
      )}
    </FormControl>
  );
};

export const DependentFieldPickerExamples = [
  {
    description:
      'Repository template selection — shows available templates based on the selected service type (string values).',
    example: {
      'ui:field': 'DependentFieldPicker',
      'ui:options': {
        controllingField: 'service_type',
        options: [
          {
            controllingValues: ['backend'],
            values: [
              'nodejs22-express',
              'python311-flask',
              'java21-springboot',
            ],
            valueNames: [
              '[Backend] Node.js (TypeScript) - Express',
              '[Backend] Python - Flask',
              '[Backend] Java - Spring Boot',
            ],
          },
          {
            controllingValues: ['cronjob'],
            values: ['nodejs20-js-cronjob', 'python311-cronjob'],
            valueNames: ['Node.js', 'Python'],
          },
        ],
      },
    },
  },
  {
    description:
      'Runtime version selection — controlled by the selected template, with object values carrying dockerfile and build-arg details.',
    example: {
      'ui:field': 'DependentFieldPicker',
      'ui:options': {
        controllingField: 'template',
        options: [
          {
            controllingValues: ['nodejs22-express'],
            values: [
              {
                dockerfile: 'devops/nodejs-ts.Dockerfile',
                'build-args': { UBI_VERSION: '10', NODEJS_VERSION: '24' },
                'container-port': '3000',
              },
              {
                dockerfile: 'devops/nodejs-ts.Dockerfile',
                'build-args': { UBI_VERSION: '10', NODEJS_VERSION: '22' },
                'container-port': '3000',
              },
            ],
            valueNames: ['Node.js 24', 'Node.js 22'],
          },
          {
            controllingValues: ['java21-springboot'],
            values: [
              {
                dockerfile: 'devops/openjdk.Dockerfile',
                'build-args': { UBI_VERSION: '9', JAVA_VERSION: '21' },
                'container-port': '8080',
              },
            ],
            valueNames: ['OpenJDK 21'],
          },
        ],
      },
    },
  },
  {
    description:
      'Cluster selection — multiple service types share the same cluster options via multiple controllingValues.',
    example: {
      'ui:field': 'DependentFieldPicker',
      'ui:options': {
        controllingField: 'service_type',
        options: [
          {
            controllingValues: ['backend', 'cronjob'],
            values: ['rosa1-ct1', 'rosa2-ct1', 'rosa3-ct1'],
            valueNames: [
              'ROSA1 (AWS - Singapore)',
              'ROSA2 (AWS - Singapore)',
              'ROSA3 (AWS - Hong Kong)',
            ],
          },
          {
            controllingValues: ['spa'],
            values: ['rosa1-ct1', 'aro1-ct3'],
            valueNames: ['ROSA1 (AWS - Singapore)', 'ARO1 (Azure - Singapore)'],
          },
        ],
      },
    },
  },
];
